import { lazy, type ReactNode } from 'react';
import type { AppModule } from '@/app/module-registry';
import { RequirePermission } from '@/core/auth/permissions';
import { AdminLayout } from './AdminLayout';

const AdminDashboardPage = lazy(() => import('./AdminDashboardPage'));
const ModerationPage = lazy(() => import('./ModerationPage'));
const UsersPage = lazy(() => import('./UsersPage'));
const RolesPage = lazy(() => import('./RolesPage'));
const AuditLogPage = lazy(() => import('./AuditLogPage'));
const FormBuilderPage = lazy(() => import('./FormBuilderPage'));
const PlansAdminPage = lazy(() => import('./PlansAdminPage'));
const ListingsAdminPage = lazy(() => import('./ListingsAdminPage'));
const ListingPreviewPage = lazy(() => import('./ListingPreviewPage'));
const SubscribersPage = lazy(() => import('./SubscribersPage'));
const SiteSettingsPage = lazy(() => import('./SiteSettingsPage'));
const ContentPage = lazy(() => import('./ContentPage'));
const CrmPage = lazy(() => import('./CrmPage'));
const ReportsPage = lazy(() => import('./ReportsPage'));
const GatewayKeysPage = lazy(() => import('./GatewayKeysPage'));

/**
 * Cada rota declara a permissão que a habilita, e o guard nega por padrão.
 *
 * Repetir a permissão aqui e no menu é intencional: são duas perguntas
 * diferentes — "mostro o link?" e "deixo entrar?" — e amarrar as duas no
 * mesmo lugar faz a segunda desaparecer quando alguém mexe na primeira.
 *
 * As duas continuam sendo apenas apresentação. Quem autoriza é o servidor.
 */
const guard = (permission: string | string[], element: ReactNode) => (
  <RequirePermission permission={permission}>{element}</RequirePermission>
);

export const adminModule: AppModule = {
  key: 'admin',
  order: 90,
  requiredFeature: 'admin',
  adminRoutes: [
    { index: true, element: guard(['listings.view', 'payments.view', 'leads.view'], <AdminDashboardPage />) },
    { path: 'imoveis', element: guard('listings.view', <ListingsAdminPage />) },

    /*
      PRE-VISUALIZACAO, com `listings.view` e nao `listings.approve`.

      Ver o anuncio e menos que decidir sobre ele: quem tria denuncia
      precisa olhar sem necessariamente poder aprovar. Os botoes de decisao
      DENTRO da tela conferem a propria permissao, um a um.
    */
    { path: 'imoveis/:id', element: guard('listings.view', <ListingPreviewPage />) },
    { path: 'moderacao', element: guard(['listings.approve', 'reports.view'], <ModerationPage />) },
    { path: 'crm', element: guard('leads.view', <CrmPage />) },
    { path: 'denuncias', element: guard('reports.view', <ReportsPage />) },
    { path: 'assinantes', element: guard('subscriptions.view', <SubscribersPage />) },
    { path: 'formularios', element: guard('forms.edit', <FormBuilderPage />) },
    { path: 'site', element: guard('settings.edit', <SiteSettingsPage />) },
    { path: 'conteudo', element: guard('content.edit', <ContentPage />) },
    { path: 'planos', element: guard('plans.view', <PlansAdminPage />) },
    { path: 'usuarios', element: guard('users.view', <UsersPage />) },
    { path: 'perfis', element: guard('roles.view', <RolesPage />) },
    { path: 'auditoria', element: guard('audit.view', <AuditLogPage />) },

    /*
      CHAVES DE PAGAMENTO — guardada por `payments.view` aqui, e por PAPEL no
      servidor.

      As duas nao sao a mesma coisa de proposito. `payments.view` mantem o link
      fora da vista de quem nao cuida de dinheiro; quem AUTORIZA e o caso de uso
      no servico de pagamentos, que exige `admin_master` e mais a senha do
      servidor. O financeiro, que tem `payments.view`, chega na tela e ve o
      aviso de que a tela nao e dele — em vez de nem saber que ela existe.
    */
    { path: 'chaves-de-pagamento', element: guard('payments.view', <GatewayKeysPage />) },
  ],
  adminLayout: AdminLayout,
};
